import {
  ComponentPropsWithoutRef,
  ForwardedRef,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import { useNavigation } from 'react-router';
import { twMerge } from 'tailwind-merge';
import Button from './Button';

type Props = Omit<ComponentPropsWithoutRef<typeof Button>, 'type'> & {
  loading?: boolean;
};

/**
 * A submit button that shows a loading spinner when the form is submitting.
 * If the `loading` prop is not provided, it will use the route loading state.
 * Disables the button when loading or disabled.
 * @see Button
 */
const Submit = forwardRef(
  (
    { children, loading, disabled, className, form, ...buttonProps }: Props,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const [showBusyIndicator, setShowBusyIndicator] = useState(false);

    const navigation = useNavigation();
    // We consider the form to be in a submission state if the navigation state is 'submitting' or 'loading' bc router doesn't go to next page until the loader is done.
    // Will need to consider if back navigation
    const isSubmission =
      navigation.state === 'submitting' || navigation.state === 'loading';
    const isSubmissionForForm = form
      ? navigation.formData?.get('formId') === form && isSubmission
      : isSubmission;
    const isPending = loading ?? isSubmissionForForm;

    // delays showing the busy state to avoid flashing for fast submissions
    useEffect(() => {
      // oxlint-disable-next-line init-declarations -- used in clearTimeout in the cleanup function that accepts undefined
      let timeout: ReturnType<typeof setTimeout>;

      if (isPending) {
        // Start timer when isPending is set to true.
        timeout = setTimeout(() => {
          setShowBusyIndicator(true);
        }, 250);
      } else {
        // Exit loading state when isPending is set to false. */
        setShowBusyIndicator(false);
      }

      return () => {
        // Clean up on unmount or when user removes isPending prop before entering loading state.
        clearTimeout(timeout);
      };
    }, [isPending]);

    return (
      <Button
        // oxlint-disable-next-line react/jsx-props-no-spreading -- since we are extending the button props
        {...buttonProps}
        className={twMerge(
          'w-full md:w-1/2',
          isPending && 'pointer-events-none',
          className,
        )}
        ref={ref}
        type="submit"
        form={form}
        disabled={disabled}
        aria-busy={showBusyIndicator}
      >
        {children}
      </Button>
    );
  },
);

Submit.displayName = 'Submit';

export default Submit;
